import { create } from "zustand";
import { persist } from "zustand/middleware";

import type { ThemeMode } from "@/theme";

type UIState = {
  themeMode: ThemeMode | "auto";
  effectiveTheme: ThemeMode;
  locale: string;
  drawerCollapsed: boolean;
  setThemeMode: (mode: ThemeMode | "auto") => void;
  setLocale: (locale: string) => void;
  setDrawerCollapsed: (collapsed: boolean) => void;
  toggleDrawerCollapsed: () => void;
  recomputeEffectiveTheme: () => void;
};

function resolveSystemTheme(): ThemeMode {
  if (typeof window === "undefined") return "light";
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      themeMode: "auto",
      effectiveTheme: resolveSystemTheme(),
      locale: import.meta.env.VITE_DEFAULT_LOCALE ?? "fr",
      drawerCollapsed: false,

      setThemeMode: (mode) => {
        const effective = mode === "auto" ? resolveSystemTheme() : mode;
        set({ themeMode: mode, effectiveTheme: effective });
      },

      setLocale: (locale) => set({ locale }),

      setDrawerCollapsed: (collapsed) => set({ drawerCollapsed: collapsed }),
      toggleDrawerCollapsed: () => set((s) => ({ drawerCollapsed: !s.drawerCollapsed })),

      recomputeEffectiveTheme: () => {
        const { themeMode } = get();
        if (themeMode === "auto") {
          set({ effectiveTheme: resolveSystemTheme() });
        }
      },
    }),
    {
      name: "cortex-ui-preferences",
      partialize: (state) => ({
        themeMode: state.themeMode,
        locale: state.locale,
        drawerCollapsed: state.drawerCollapsed,
      }),
    },
  ),
);
